// Module: usePanelInstrument.js
// Purpose: Live Analysis right-side panel (board 50-6, package P8b) — resolves the FULL
//          instrument-master record for the deployment's resolved instrument id (never
//          `config.instruments[0]`, which can be a stale pre-rollover contract, D36/02-design.md
//          §E). Guards stale responses with a request-id ref AND clears `instrument` to `null`
//          the INSTANT the resolved id changes, before the new fetch resolves (V2-L7).

import { useEffect, useRef, useState } from 'react';
import { services } from '../../../services/api';
import { liveInstrumentIdOf } from '../../../contexts/TradingDataContext';

/**
 * @param {object} deployment
 * @returns {{ instrument: object|null, instrumentLoading: boolean, instrumentError: Error|null }}
 */
export function usePanelInstrument(deployment) {
  const instrumentId = liveInstrumentIdOf(deployment);

  const [instrument, setInstrument] = useState(null);
  const [instrumentLoading, setInstrumentLoading] = useState(false);
  const [instrumentError, setInstrumentError] = useState(null);

  //! Request-id guard (V2-L7) — only the MOST RECENTLY issued request's resolution is ever
  //! allowed to write state; an older, out-of-order-resolving fetch for a stale id is discarded.
  const requestIdRef = useRef(0);

  useEffect(() => {
    //--> Clear to null the INSTANT the id changes — before the new fetch even starts, let alone
    //*   resolves — so a stale instrument never lingers across an instrument-id transition.
    setInstrument(null);
    setInstrumentError(null);

    if (!instrumentId) {
      setInstrumentLoading(false);
      return;
    }

    const myRequestId = ++requestIdRef.current;
    setInstrumentLoading(true);

    services.instrumentMaster.getInstrument(instrumentId)
      .then((res) => {
        if (requestIdRef.current !== myRequestId) return; // stale response discarded
        setInstrument(res?.data || null);
        setInstrumentLoading(false);
      })
      .catch((err) => {
        if (requestIdRef.current !== myRequestId) return;
        console.error('[usePanelInstrument] instrument fetch failed', err);
        setInstrumentError(err);
        setInstrumentLoading(false);
      });
  }, [instrumentId]);

  return { instrument, instrumentLoading, instrumentError };
}
